import omitDeep from "omit-deep-lodash";
import * as yup from "yup";
import {
  ChamberInput,
  GetChamberQuery,
  UpdateChamberMutation,
} from "../../../../graphql/types";
import {
  VALIDATION_MESSAGES,
  WEBSITE_REGEX,
} from "../../../../utils/validationPatterns";

export const CHAMBER_DESCRIPTION_CHARACTER_LIMIT = 2000;

export type ChamberProfileFormValues = ChamberInput;

type ChamberProfileSource =
  | NonNullable<GetChamberQuery["getChamber"]>
  | NonNullable<UpdateChamberMutation["updateChamber"]>;

const optionalWebsite = yup
  .string()
  .test(
    "optional-website",
    VALIDATION_MESSAGES.WEBSITE_INVALID,
    (value) => !value?.trim() || WEBSITE_REGEX.test(value.trim()),
  );

const normalizeBanners = (
  banners:
    | ChamberProfileSource["profileBanner"]
    | ChamberProfileSource["directoryBanner"],
) =>
  banners
    ? [...banners]
        .sort((a, b) => a.position - b.position)
        .map((banner, index) => ({ ...banner, position: index + 1 }))
    : undefined;

export const chamberProfileValidationSchema = yup.object({
  name: yup.string().required("Chamber name is required"),
  description: yup
    .string()
    .max(
      CHAMBER_DESCRIPTION_CHARACTER_LIMIT,
      `Chamber description cannot exceed ${CHAMBER_DESCRIPTION_CHARACTER_LIMIT} characters`,
    ),
  website: yup
    .string()
    .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID)
    .required("Chamber website is required"),
  supportContactName: yup
    .string()
    .required("Administrator Contact Name is required"),
  supportContactEmail: yup
    .string()
    .email("Please enter a valid email")
    .required("Administrator Contact Email is required"),
  billingContactName: yup.string().required("Billing Contact Name is required"),
  billingContactEmail: yup
    .string()
    .email("Please enter a valid email")
    .required("Billing Contact Email is required"),
  signingAuthorityContactName: yup
    .string()
    .required("Signing Authority Contact Name is required"),
  signingAuthorityContactEmail: yup
    .string()
    .email("Please enter a valid email")
    .required("Signing Authority Contact Email is required"),
  secondarySupportEmail: yup.string().email("Please enter a valid email"),
  chamberSupportPageUrl: optionalWebsite,
  address: yup.object({
    streetAddress: yup.string().required("Street address is required"),
    city: yup.string().required("City is required"),
    province: yup.string().required("Province/State is required"),
    postalCode: yup.string().required("Postal/Zip code is required"),
  }),
  supportLink: optionalWebsite,
  jobPostingLink: optionalWebsite,
  eventsLink: optionalWebsite,
  newsletterLink: optionalWebsite,
  surveyLink: optionalWebsite,
  advocacyWinsLink: optionalWebsite,
  callForNominationsLink: optionalWebsite,
  memberSignupLink: optionalWebsite,
  sponsorLogoLink: optionalWebsite,
  facebook: optionalWebsite,
  instagram: optionalWebsite,
  threads: optionalWebsite,
  linkedin: optionalWebsite,
  twitter: optionalWebsite,
  tiktok: optionalWebsite,
  youtube: optionalWebsite,
});

export const createChamberProfileInitialValues = ({
  chamber,
}: {
  chamber?: ChamberProfileSource | null;
}): ChamberProfileFormValues => {
  const initialValues: ChamberProfileFormValues = {
    id: chamber?.id || "",
    name: chamber?.name || "",
    description: chamber?.description || "",
    website: chamber?.website || "",
    supportContactEmail: chamber?.supportContactEmail || "",
    supportContactName: chamber?.supportContactName || "",
    billingContactName: chamber?.billingContactName || "",
    billingContactEmail: chamber?.billingContactEmail || "",
    signingAuthorityContactName: chamber?.signingAuthorityContactName || "",
    signingAuthorityContactEmail: chamber?.signingAuthorityContactEmail || "",
    supportLink: chamber?.supportLink || "",
    secondarySupportEmail: chamber?.secondarySupportEmail || "",
    chamberSupportPageUrl: chamber?.chamberSupportPageUrl || "",
    phoneNumber: chamber?.phoneNumber || "",
    phoneNumberExtension: chamber?.phoneNumberExtension || "",
    facebook: chamber?.facebook || "",
    instagram: chamber?.instagram || "",
    threads: chamber?.threads || "",
    linkedin: chamber?.linkedin || "",
    twitter: chamber?.twitter || "",
    tiktok: chamber?.tiktok || "",
    youtube: chamber?.youtube || "",
    jobPostingLink: chamber?.jobPostingLink || "",
    eventsLink: chamber?.eventsLink || "",
    newsletterLink: chamber?.newsletterLink || "",
    surveyLink: chamber?.surveyLink || "",
    advocacyWinsLink: chamber?.advocacyWinsLink || "",
    callForNominationsLink: chamber?.callForNominationsLink || "",
    memberSignupLink: chamber?.memberSignupLink || "",
    address: {
      city: chamber?.address.city || "",
      postalCode: chamber?.address.postalCode || "",
      province: chamber?.address.province || "",
      streetAddress: chamber?.address.streetAddress || "",
    },
    logo: chamber?.logo || undefined,
    sponsorLogo: chamber?.sponsorLogo || undefined,
    sponsorLogoLink: chamber?.sponsorLogoLink || "",
    directoryBanner: normalizeBanners(chamber?.directoryBanner),
    profileBanner: normalizeBanners(chamber?.profileBanner),
  };

  return omitDeep(initialValues, "__typename") as ChamberProfileFormValues;
};

const normalizeEmail = (value?: string | null) =>
  (value || "").trim().toLowerCase();

export const createChamberProfileUpdateInput = ({
  values,
}: {
  values: ChamberProfileFormValues;
}): ChamberInput => ({
  id: values.id,
  name: values.name,
  description: values.description,
  website: values.website,
  supportContactName: values.supportContactName,
  supportContactEmail: normalizeEmail(values.supportContactEmail),
  billingContactName: values.billingContactName,
  billingContactEmail: normalizeEmail(values.billingContactEmail),
  signingAuthorityContactName: values.signingAuthorityContactName,
  signingAuthorityContactEmail: normalizeEmail(
    values.signingAuthorityContactEmail,
  ),
  phoneNumber: values.phoneNumber,
  phoneNumberExtension: values.phoneNumberExtension,
  supportLink: values.supportLink,
  secondarySupportEmail: normalizeEmail(values.secondarySupportEmail),
  chamberSupportPageUrl: values.chamberSupportPageUrl,
  address: {
    streetAddress: values.address.streetAddress,
    city: values.address.city,
    province: values.address.province,
    postalCode: values.address.postalCode,
  },
  facebook: values.facebook,
  instagram: values.instagram,
  threads: values.threads,
  linkedin: values.linkedin,
  twitter: values.twitter,
  tiktok: values.tiktok,
  youtube: values.youtube,
  eventsLink: values.eventsLink,
  newsletterLink: values.newsletterLink,
  jobPostingLink: values.jobPostingLink,
  surveyLink: values.surveyLink,
  advocacyWinsLink: values.advocacyWinsLink,
  memberSignupLink: values.memberSignupLink,
  callForNominationsLink: values.callForNominationsLink,
  logo: values.logo,
  sponsorLogo: values.sponsorLogo,
  sponsorLogoLink: values.sponsorLogoLink,
  profileBanner: values.profileBanner,
  directoryBanner: values.directoryBanner,
});
